import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { languageEnglish } from "../../i18n";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getLanguageFromPathname } from "@utils/slugUtils";
import { LanguageSwitch } from "./LanguageSwitch";
import { ThemeSwitch } from "./ThemeSwitch";
import { useUserContext } from "@hooks/useUserContext";
import { Alert } from "@components/Alert";

interface ControlCenterProps {
  translatedSlug: string;
}

export const ControlCenter = ({ translatedSlug }: ControlCenterProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Detect language from pathname because :lang can be any slug on subpages
  const currentLanguage =
    searchParams.get("_storyblok_lang") ||
    getLanguageFromPathname(location.pathname);

  const { user } = useUserContext();

  // If there is no user, then something went wrong
  if (!user) {
    // show error message
    return (
      <Alert
        labelText={t("ControlCenter.error.label")}
        text={t("ControlCenter.error.message")}
        variant="error"
        className="min-w-96"
        textClassName="text-tertiary-100"
      />
    );
  }

  const { themePreference: currentTheme, languagePreference } = user;

  return (
    <>
      <div className="flex w-56 flex-col gap-2">
        <Typography size="sm" className="leading-[120%] text-white">
          {t("ControlCenter.language")}
        </Typography>
        <LanguageSwitch
          translatedSlug={translatedSlug}
          languagePreference={languagePreference}
        />
      </div>
      <Button
        variant="soft"
        id="other-languages"
        size="lg"
        endIcon="ChevronRight"
        className="mt-4 w-full gap-1 text-sm"
        href={
          currentLanguage === languageEnglish
            ? `/${languageEnglish}/settings`
            : "/settings"
        }
      >
        {t("Settings.otherLanguages")}
      </Button>
      <div className="mt-4 flex w-56 flex-col gap-2">
        <Typography size="sm" className="leading-[120%] text-white">
          {t("ControlCenter.display")}
        </Typography>
        <ThemeSwitch currentTheme={currentTheme} />
      </div>
      <Button
        variant="soft"
        id="all-settings"
        size="lg"
        endIcon="ChevronRight"
        className="mt-4 w-full gap-1 text-sm"
        href={
          currentLanguage === languageEnglish
            ? `/${languageEnglish}/settings`
            : "/settings"
        }
      >
        {t("Settings.allSettings")}
      </Button>
    </>
  );
};
